/**
 * THE DATA GATE — a send stops when a division's DE row cannot answer a mandatory
 * column, or answers it with something that is not a valid value.
 *
 * Layne, BUG-CONTACTUS-TOKENS-NOT-RENDERED-20260909: *"can a no-row lookup raise a
 * validation error and block the send, instead of only being visible in preview? … If
 * they can be told apart, no-row should stop the email rather than rely on someone
 * checking."*
 *
 * ── WHY THIS IS NOT `required: true` ───────────────────────────────────────────────
 * The obvious implementation is the author-facing `required` flag, and it was rejected
 * twice, by her, for the right reason. All 45 divisions have rows, so a blank
 * `ContactUrl` no longer has a plausible DATA cause — the only thing left that produces
 * one is the lookup not answering. `required: true` would therefore fire almost
 * exclusively during an outage, turning a Hub problem into a company-wide send freeze.
 * Her rule, worth keeping: **a proxy gate gets more dangerous as the data gets
 * cleaner.** When rows were patchy, blankness had many causes and gating on it was
 * roughly right; now blankness means one thing, and not the thing the gate would claim
 * to catch.
 *
 * So this gate never fires on a state that infrastructure can produce:
 *
 *   row `undefined`   the route has not answered           → SILENT
 *   row `DE_FAILED`   the route failed (outage, !ok, threw) → SILENT
 *   row `null`        answered: this division has no row    → BLOCKS
 *   row present       answered: mandatory column empty      → BLOCKS
 *   row present       answered: value fails its pattern     → BLOCKS
 *
 * The `undefined`/`DE_FAILED` split is what makes that safe, and it did not exist until
 * 2026-09-09 — `useEchDyn` collapsed a failed fetch into the same `null` as a genuine
 * no-row, so this gate built a day earlier would have frozen every send during an
 * outage. See `EchCtx.deRows`.
 *
 * ── IT FIRES ON THE EFFECTIVE VALUE, NOT ON THE COLUMN ─────────────────────────────
 * The escape hatch is the whole reason blocking is defensible: `ContactUrl` is
 * `authorEditable`, so a requester hit by this can type a link and move on. That only
 * works if the gate reads what the block WILL render — the requester's value over the
 * row's, exactly as `contactLookup` resolves it — rather than the raw column. Reading
 * the column would leave the block unclearable: type a perfectly good link and the bad
 * row keeps blocking you. **The row state chooses the MESSAGE; the effective value
 * decides whether to block at all.**
 *
 * ── OPT-IN PER FIELD ───────────────────────────────────────────────────────────────
 * `dataRequired: true` on the field, and nothing else gates. Layne's column audit is
 * why: of the Contact Us block's 18 `fromColumn` fields, 17 are legitimately empty for
 * most divisions (the five headshot alts in all 45 — decorative by her 8 September
 * ruling — `Cta1_Label` and both `Cta2_*` in 44, the four group columns in 37–38, the
 * five headshot URLs in 27–39). Only `Cta1_Url` is populated in all 45. A gate on "any
 * declared column empty" would have blocked 45 of 45 — the same shape as
 * `BLOCKER-REQUIRED-BY-TYPE-20260902`, where declaring those 18 fields stopped every
 * author submitting.
 *
 * Author-facing `required` cannot carry the flag: it is deliberately `false` on all 18.
 *
 * ── AND IT MAKES A PATTERN BLOCK, ON THIS FIELD ONLY ───────────────────────────────
 * A `pattern` is a WARNING everywhere else and must stay one (`guardrails.ts` — "an
 * over-strict pattern must not be able to stop a send", after Layne's first one
 * rejected `$5,000 toward your closing costs` and any two-line headline). 88 patterns
 * are declared, 68 on url/number/tel; flipping them all to blocking re-opens that
 * ruling wholesale. So `dataRequired` doubles as the switch — this field must have a
 * value AND the value must be the right shape — and the other 87 patterns are
 * untouched. `patternBlocks: true` is accepted as a synonym in case she prefers the two
 * decoupled.
 */
import { echMeta } from "./echRegistry";
import { esc } from "../email/core";
import { deFailed, type DeRowState } from "./echDyn";
import type { RequiredBlank } from "./echRequired";
import type { AnyBlockInstance } from "../email/document";

/** the row for one source, as `EchCtx.deRows` has it */
type Rows = Record<string, DeRowState> | undefined;

/** what this gate needs off the ctx — deliberately narrow, so it is trivial to test */
export interface DataGateCtx {
  deRows?: Rows;
  divisionCode?: string;
}

/** a field's declaration, only the keys this gate reads */
interface GateField {
  fromColumn?: string;
  label?: string;
  pattern?: string;
  patternMessage?: string;
  dataRequired?: boolean;
  patternBlocks?: boolean;
}

/**
 * HER SENTENCES, AS THE FALLBACK ONLY (2026-09-09).
 *
 * Layne: *"read these from the block rather than holding them in code… if they live in
 * your renderer, we can't reword them without a deploy, and we've now been round that
 * loop three times."* Agreed — `dataSource.messages` wins whenever it is there.
 *
 * These exist anyway, and that is not a hedge on her wording. `BUG-CONTACTUS-
 * PLACEHOLDER-20260908` was four `-->` characters inside this same header's note
 * strings stopping it from parsing, which left the block with no fields, no data source
 * and no treatments. Gate copy that lives ONLY in the header means an unparsed header
 * yields a send-blocking gate with no message in it — or an argument for not blocking.
 * **Externalising the copy must never externalise the decision to block.**
 */
export const DATA_GATE_DEFAULTS = {
  noRow: "{{Division}} has no row in {{DataSource}} — enter a {{Field}} to send, and ask for the row to be added.",
  emptyColumn: "{{Division}}'s row has no {{Column}} — enter a {{Field}} to send, and ask for the column to be filled.",
  invalidValue: "{{Division}}'s {{Column}} isn't a valid link ({{Value}}) — enter a {{Field}} to send, and ask for the row to be fixed.",
} as const;

export type DataGateMessageKey = keyof typeof DATA_GATE_DEFAULTS;

/** a bad value is worth showing to whoever fixes the row, but it is untrusted and can be
 *  a 400-character tracking URL — so escaped, and clipped to fit a gate popover */
const VALUE_MAX = 60;
export function clipValue(v: string): string {
  const t = String(v == null ? "" : v).trim();
  return esc(t.length > VALUE_MAX ? t.slice(0, VALUE_MAX - 1) + "…" : t);
}

/**
 * Fill a message's `{{Token}}`s. Same syntax the block's markup already uses, because
 * her sentences named `HOU` and `Cta1_Url` literally and both vary — the division on
 * every send, the column as soon as a second field declares `dataRequired`. An unknown
 * token is left as written, exactly as in the markup, so a typo stays visible.
 */
const TOKEN_ALIASES: Record<string, "Division" | "DataSource" | "Column" | "Field" | "Value"> = {
  division: "Division", div: "Division",
  datasource: "DataSource", de: "DataSource", data_source: "DataSource",
  column: "Column", col: "Column",
  field: "Field", label: "Field",
  value: "Value", val: "Value",
};

export function fillMessage(tpl: string, vals: Partial<Record<"Division" | "DataSource" | "Column" | "Field" | "Value", string>>): string {
  /* ── BOTH BRACE STYLES, CASE-INSENSITIVE (2026-09-09) ─────────────────────────
     I specified `{{Division}}`; Layne wrote `{division}`, `{de}`, `{column}` and
     offered to rewrite. Accepting hers instead: single braces read better in a
     sentence, these are messages rather than markup so there is no collision with
     the block's own tokens, and `{de}` is what she calls the data extension. */
  return String(tpl || "").replace(/\{\{([A-Za-z0-9_]+)\}\}|\{([A-Za-z0-9_]+)\}/g, (whole, a: string, b: string) => {
    const raw = a || b;
    const canon = TOKEN_ALIASES[String(raw).toLowerCase()];
    const v = canon ? (vals as Record<string, string | undefined>)[canon] : undefined;
    return v === undefined ? whole : v;
  });
}

/**
 * ── HER KEY NAMES, NOT MINE (2026-09-09) ──────────────────────────────────────────
 * Layne declared `noRow` / `emptyRequiredColumn` / `invalidFedValue`; I had specified
 * `noRow` / `emptyColumn` / `invalidValue`. Only the first matched, so two of her three
 * sentences would have fallen back to the code defaults SILENTLY — the message would
 * still have been right, which is exactly what makes that class of mismatch expensive
 * to notice. Both spellings are accepted rather than asking her to rewrite: her names
 * are the better ones anyway (they say WHICH column and WHOSE value).
 */
const KEY_ALIASES: Record<DataGateMessageKey, string[]> = {
  noRow: ["noRow", "norow", "no_row"],
  emptyColumn: ["emptyColumn", "emptyRequiredColumn", "emptycolumn", "empty_column", "empty_required_column"],
  invalidValue: ["invalidValue", "invalidFedValue", "invalidvalue", "invalid_value", "invalid_fed_value"],
};

/** the declared copy for one case, or the code default — `blockMessages` accepted too */
export function gateMessage(ds: unknown, key: DataGateMessageKey): string {
  const d = (ds || {}) as { messages?: Record<string, unknown>; blockMessages?: Record<string, unknown> };
  const bag = { ...(d.blockMessages || {}), ...(d.messages || {}) } as Record<string, unknown>;
  const lower: Record<string, unknown> = {};
  for (const k of Object.keys(bag)) lower[k.toLowerCase()] = bag[k];
  for (const name of KEY_ALIASES[key]) {
    const v = lower[name.toLowerCase()];
    if (typeof v === "string" && v.trim()) return v;
  }
  return DATA_GATE_DEFAULTS[key];
}

/**
 * ── WARN ON THE FIRST RUN, BLOCK AFTER (Layne's ask, 2026-09-09) ──────────────────
 * *"I checked the import file rather than a live DE read, and the DE has been edited
 * since for `Cta1_Label`. If your gate can log rather than block on the first run,
 * that'd confirm it against live data before it starts stopping sends."*
 *
 * `dataSource.gateMode: "warn"` puts the same sentence in the block-wide warnings list
 * instead of the Done gate — nothing is stopped, and a division that would have been
 * blocked is visible in the Studio. `"block"` is the default, and the switch lives in
 * the header so she flips it without a deploy.
 *
 * What this is NOT: a central audit. Warn mode only speaks when someone happens to build
 * an email for the affected division, so it confirms live data slowly and by accident.
 * The direct answer to her question is a one-off read of all 45 rows against the pattern.
 */
export function gateMode(ds: unknown): "warn" | "block" {
  const m = String(((ds || {}) as { gateMode?: unknown }).gateMode || "").trim().toLowerCase();
  return m === "warn" || m === "log" ? "warn" : "block";
}

/**
 * Every send-blocking data fault on the canvas, as `RequiredBlank` rows.
 *
 * The SAME shape the Done gate already consumes, on purpose: the count, the popover,
 * the cherry ring and the jump-to-field all work unchanged, and the Studio, the form's
 * submit belt and the Inspector's markers cannot disagree about what is missing — which
 * is the standing reason `requiredBlanks` has one implementation and three callers.
 * `kind: "data"` so nothing reads these as a character cap.
 */
export function dataGateFindings(blocks: AnyBlockInstance[], ctx: DataGateCtx | undefined): { blocking: RequiredBlank[]; warning: RequiredBlank[] } {
  const all = dataGateRows(blocks, ctx);
  return {
    blocking: all.filter((r) => r.mode === "block").map((r) => r.row),
    warning: all.filter((r) => r.mode === "warn").map((r) => r.row),
  };
}

/** the blocking half — what the Done gate and the submit belt consume */
export function dataGateBlanks(blocks: AnyBlockInstance[], ctx: DataGateCtx | undefined): RequiredBlank[] {
  return dataGateFindings(blocks, ctx).blocking;
}

function dataGateRows(blocks: AnyBlockInstance[], ctx: DataGateCtx | undefined): { row: RequiredBlank; mode: "warn" | "block" }[] {
  const out: { row: RequiredBlank; mode: "warn" | "block" }[] = [];
  const rows = ctx && ctx.deRows;
  if (!rows) return out;                       /* nothing has answered yet */
  const div = String((ctx && ctx.divisionCode) || "").toUpperCase();

  for (const b of blocks || []) {
    const meta = echMeta(b.type);
    const full = meta && meta.full;
    const ds = full && (full as unknown as { dataSource?: { externalKey?: string; name?: string } }).dataSource;
    if (!ds || !ds.externalKey) continue;

    const row = rows[ds.externalKey];
    if (row === undefined || deFailed(row)) continue;   /* the two infrastructure states */

    const fields = (full && full.fields) as Record<string, GateField> | undefined;
    if (!fields) continue;
    const blockLabel = String((meta.gr && meta.gr.label) || b.type);
    const data = (b.data || {}) as Record<string, unknown>;

    for (const k of Object.keys(fields)) {
      const f = fields[k] || {};
      if (!f.dataRequired && !f.patternBlocks) continue;   /* opt-in, per field */

      const col = String(f.fromColumn || "").trim();
      const fromRow = row && col ? String(row[col.toLowerCase()] ?? "") : "";
      const typed = String(data[k] == null ? "" : data[k]).trim();
      /* the value the block WILL render — the requester's over the row's, matching
         `contactLookup`. Reading `fromRow` alone would make the block unclearable. */
      const effective = typed !== "" ? typed : fromRow.trim();
      /* did the author override the feed? decided by comparison, not by `__socFed`,
         which the DE fill does not write */
      const isTyped = typed !== "" && typed !== fromRow.trim();

      const tokens = {
        Division: div || "This division",
        DataSource: String(ds.name || "the data extension"),
        Column: col || k,
        Field: String(f.label || k),
      };
      const mode = gateMode(ds);
      const push = (message: string) =>
        out.push({ row: { id: b.id, type: b.type, block: blockLabel, k, field: message, kind: "data" }, mode });

      if (!effective) {
        /* blank: the row state picks the wording, per her three sentences */
        push(fillMessage(gateMessage(ds, row === null ? "noRow" : "emptyColumn"), tokens));
        continue;
      }
      if (!f.pattern) continue;                 /* nothing to validate the shape against */
      let ok = true;
      try { ok = new RegExp(f.pattern).test(effective); } catch { ok = true; }
      /* an unparseable pattern must never block — same instinct as the warn-only rule
         it is being promoted out of */
      if (ok) continue;
      /* WHO wrote the bad value decides the copy. A typed one is the requester's own
         input, so it gets the field's `patternMessage` — already hers, already declared.
         A fed one is a data fault and gets her `invalidValue` sentence, which names the
         row rather than blaming the person reading it. */
      push(isTyped
        ? String(f.patternMessage || "").trim() || fillMessage(gateMessage(ds, "invalidValue"), { ...tokens, Value: clipValue(effective) })
        : fillMessage(gateMessage(ds, "invalidValue"), { ...tokens, Value: clipValue(effective) }));
    }
  }
  return out;
}

/**
 * Is any data source a block on the canvas depends on still unanswered?
 *
 * Layne, 2026-09-09, on the hole in the design above: silence while the lookup is
 * pending is safe against a false BLOCK and is a false PASS on Done — a requester who
 * clicks Done in the first second gets a clean gate because the gate does not exist yet,
 * and ships the dead link. So Done waits instead of passing.
 *
 * Scoped to sources the CANVAS actually needs, not `ctx.pending`: a global pending flag
 * would hold Done on a fresh Studio for reasons that have nothing to do with this block.
 * `DE_FAILED` is NOT unanswered — an error releases Done as clean, because an error is
 * infrastructure and this gate never decides anything on infrastructure.
 */
export function dataGatePending(blocks: AnyBlockInstance[], ctx: DataGateCtx | undefined): boolean {
  const rows = ctx && ctx.deRows;
  for (const b of blocks || []) {
    const meta = echMeta(b.type);
    /* ── AN UNCLASSIFIED SFMC BLOCK HOLDS DONE TOO (2026-09-09) ──────────────────
       Found by the reachability test, and it is the same false pass one layer
       earlier: before the block library has been pulled, `echMeta` knows nothing
       about `sfmc_1028595`, so the canvas looks like it contains no data-source
       block at all and Done passed cleanly in the first moments of a fresh Studio —
       exactly the window Layne's "Done waits" ruling exists to close. A block whose
       type is an SFMC block but which has no registry entry yet is not "not gating",
       it is "not yet known", and the honest answer is to wait. Self-clears the
       instant the registry lands. */
    if (!meta) { if (ECH_TYPE_RE.test(String(b.type || ""))) return true; continue; }
    const full = meta.full;
    const ds = full && (full as unknown as { dataSource?: { externalKey?: string } }).dataSource;
    if (!ds || !ds.externalKey) continue;
    const fields = (full && full.fields) as Record<string, GateField> | undefined;
    /* only a block that actually gates can hold Done */
    if (!fields || !Object.keys(fields).some((k) => fields[k] && (fields[k].dataRequired || fields[k].patternBlocks))) continue;
    if (!rows || rows[ds.externalKey] === undefined) return true;
  }
  return false;
}

/** an SFMC-sourced block's instance type — `sfmc_<assetId>`, as `echRegistry` keys them */
const ECH_TYPE_RE = /^sfmc_/i;
